import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';

import { Breadcrumb } from '../components/Breadcrumb';
import { CustomerDemandSection } from '../components/partDemand/CustomerDemandSection';
import { DemandChart } from '../components/partDemand/DemandChart';
import { DemandTransactionsTable } from '../components/partDemand/DemandTransactionsTable';
import { ForecastSection } from '../components/partDemand/ForecastSection';
import { DEMAND_COLORS } from '../components/partDemand/format';
import { PartDemandSummary } from '../components/partDemand/PartDemandSummary';
import { PartPicker } from '../components/partDemand/PartPicker';
import { PlanningSection } from '../components/partDemand/PlanningSection';
import { formatDate } from '../components/quoteToCash/format';
import { LineChart, TooltipRow } from '../components/quoteToCash/LineChart';
import {
  SectionCard,
  SectionState,
} from '../components/quoteToCash/SectionCard';
import { apiService } from '../services/apiService';
import { PartDemand } from '../services/partDemandTypes';

/**
 * Part Demand & Forecast: one part's true consumption (shipments and job issues, less returns) since Epicor went
 * live, its customers, a 3/6/12-month forecast with its confidence, and its stock and planning context
 */
export const PartDemandPage: React.FC = () => {
  const { partNum } = useParams<{ partNum: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [state, setState] = useState<SectionState<PartDemand>>({
    data: null,
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (!partNum) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let active = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    apiService.getPartDemand(partNum).then(
      (data) => active && setState({ data, loading: false, error: null }),
      (error: Error) =>
        active && setState({ data: null, loading: false, error: error.message })
    );
    return () => {
      active = false;
    };
  }, [partNum]);

  const data = state.data;
  // Each section shows the part's one response; loading and errors are shown once, above them
  const loaded: SectionState<PartDemand> = { ...state, error: null };
  const card = (
    title: string,
    subtitle: string | undefined,
    body: (d: PartDemand) => React.ReactNode
  ) => (
    <SectionCard
      title={title}
      subtitle={subtitle}
      state={loaded}
      onRetry={() => undefined}
    >
      {body}
    </SectionCard>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 w-full">
      <Breadcrumb currentPage={t('navigation.partDemand')} />
      <div className="mb-6 mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('partDemand.title')}
          </h1>
          <p className="mt-2 text-gray-600">{t('partDemand.subtitle')}</p>
        </div>
        {partNum && (
          <Link
            to={`/parts/${encodeURIComponent(partNum)}`}
            className="text-sm font-medium text-blue-700 hover:underline"
          >
            {t('partDemand.partDetailsLink')}
          </Link>
        )}
      </div>

      <div className="mb-6">
        <PartPicker
          initial={partNum ?? ''}
          onSelect={(p) => navigate(`/part-demand/${encodeURIComponent(p)}`)}
        />
      </div>

      {state.error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800">{state.error}</p>
        </div>
      )}
      {!partNum && (
        <p className="text-gray-600">{t('partDemand.picker.prompt')}</p>
      )}
      {partNum && !data && state.loading && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">{t('common.loading')}</p>
        </div>
      )}

      {data && (
        <div className={state.loading ? 'opacity-50 transition-opacity' : ''}>
          <PartDemandSummary data={data} />
          {data.kpis.lastDemandDate === null && (
            <p className="mb-6 p-4 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-800">
              {t('partDemand.noDemand')}
            </p>
          )}
          <div className="space-y-6">
            {card(
              t('partDemand.chart.title'),
              t('partDemand.chart.subtitle'),
              (d) => (
                <>
                  <DemandChart monthly={d.monthly} uom={d.part.uom} />
                  <h3 className="mt-6 mb-1 text-sm font-semibold text-gray-900">
                    {t('partDemand.chart.eventsTitle')}
                  </h3>
                  <p className="mb-2 text-sm text-gray-600">
                    {t('partDemand.chart.eventsSubtitle')}
                  </p>
                  <LineChart
                    ariaLabel={t('partDemand.chart.eventsTitle')}
                    height={160}
                    labels={d.monthly.map((m) =>
                      formatDate(`${m.month}-01`, lang, 'monthYear')
                    )}
                    formatValue={(v) => (Number.isInteger(v) ? String(v) : '')}
                    series={[
                      {
                        key: 'events',
                        label: t('partDemand.chart.events'),
                        color: DEMAND_COLORS.events,
                        values: d.monthly.map((m) => m.demandEvents),
                      },
                    ]}
                    renderTooltip={(i) => (
                      <>
                        <TooltipRow
                          color={DEMAND_COLORS.events}
                          value={String(d.monthly[i].demandEvents)}
                          label={t('partDemand.chart.events')}
                        />
                        <TooltipRow
                          value={String(d.monthly[i].distinctCustomers)}
                          label={t('partDemand.chart.customers')}
                        />
                      </>
                    )}
                  />
                </>
              )
            )}
            {card(
              t('partDemand.forecast.title'),
              t('partDemand.forecast.subtitle'),
              (d) => (
                <ForecastSection data={d} />
              )
            )}
            {card(
              t('partDemand.customers.title'),
              t('partDemand.customers.subtitle', {
                count: data.kpis.analysisMonths,
              }),
              (d) => (
                <CustomerDemandSection data={d} />
              )
            )}
            {card(
              t('partDemand.planning.title'),
              t('partDemand.planning.subtitle'),
              (d) => (
                <PlanningSection data={d} />
              )
            )}
            {card(
              t('partDemand.transactions.title'),
              t('partDemand.transactions.subtitle'),
              (d) => (
                <DemandTransactionsTable
                  transactions={d.transactions}
                  uom={d.part.uom}
                />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};
